import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Calculator, Check, Loader2, Sparkles, Phone, Mail, User, Home, Building, Building2, Hammer, Frame, Layers, Anchor, BrickWall, Warehouse, SquareStack, Square, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY } from "@/data/content";

const STEPS = ["Gebäudetyp", "Bauweise", "Größe & Standort", "Qualität & Extras", "Kontakt"];

const GEBAEUDE = [
  { id: "einfamilienhaus", label: "Einfamilienhaus", icon: Home, hint: "Privater Hausbau" },
  { id: "mehrfamilienhaus", label: "Mehrfamilienhaus", icon: Building, hint: "Mehrere Wohneinheiten" },
  { id: "gewerbe", label: "Gewerbebau", icon: Building2, hint: "Halle, Büro, Industrie" },
  { id: "sanierung", label: "Sanierung / Anbau", icon: Hammer, hint: "Bestand modernisieren" },
];

const BAUART = [
  { id: "massiv", label: "Massivbau", hint: "Mauerwerk / Stahlbeton" },
  { id: "holz", label: "Holzbau", hint: "Holzrahmen / KVH" },
  { id: "hybrid", label: "Hybrid", hint: "Kombiniert" },
];

const QUALITAET = [
  { id: "standard", label: "Standard", hint: "Solide Markenmaterialien" },
  { id: "premium", label: "Premium", hint: "Gehobene Ausführung" },
  { id: "luxus", label: "Luxus", hint: "Sichtbeton SB4 · KS-Sichtmauerwerk" },
];

const STANDORT = ["Köln", "Bonn", "Düsseldorf", "Leverkusen", "Aachen", "Wuppertal", "Essen", "Dortmund"];

const ZUSATZ = [
  { id: "kellerbau", label: "Kellerbau", icon: Warehouse },
  { id: "bodenplatte", label: "Bodenplatte (WU)", icon: Square },
  { id: "fundamentarbeiten", label: "Fundamente", icon: Anchor },
  { id: "betonarbeiten", label: "Betonarbeiten", icon: Layers },
  { id: "schalungsarbeiten", label: "Schalung", icon: Frame },
  { id: "mauerarbeiten", label: "Mauerwerk", icon: BrickWall },
  { id: "deckenarbeiten", label: "Decken", icon: SquareStack },
  { id: "abrissarbeiten", label: "Abriss", icon: Wrench },
];

function fmt(n) {
  return new Intl.NumberFormat("de-DE").format(Math.round(n || 0));
}

export default function AICalculator({ compact = false }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    gebaeudetyp: "einfamilienhaus",
    bauart: "massiv",
    quadratmeter: 160,
    standort: "Köln",
    qualitaetsstufe: "premium",
    leistungen: ["bodenplatte"],
    bauweise: "neubau",
  });
  const [contact, setContact] = useState({ name: "", email: "", telefon: "", consent: false });
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const progress = useMemo(() => ((step + 1) / STEPS.length) * 100, [step]);

  const toggleZusatz = (id) =>
    setData((d) => ({
      ...d,
      leistungen: d.leistungen.includes(id) ? d.leistungen.filter((x) => x !== id) : [...d.leistungen, id],
    }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const calculate = async () => {
    setCalculating(true);
    try {
      const { data: res } = await api.post("/calculator/estimate", {
        input: data,
        contact: {
          name: contact.name,
          email: contact.email || null,
          telefon: contact.telefon,
        },
      });
      setResult(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCalculating(false);
    }
  };

  const submitLead = async () => {
    if (!contact.name || !contact.email) {
      toast.error("Bitte Name und E-Mail eingeben.");
      return;
    }
    if (!contact.consent) {
      toast.error("Bitte Datenschutz akzeptieren.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: contact.name,
        email: contact.email,
        telefon: contact.telefon,
        nachricht: `Anfrage über KI-Preisrechner. Standort: ${data.standort}. Wunschtermin: zeitnah.`,
        projekttyp: data.gebaeudetyp,
        quelle: "preisrechner",
        rueckruf: true,
        estimate: result?.estimate || null,
        calculator_input: data,
      });
      toast.success("Vielen Dank! Wir melden uns binnen 24h.");
      setStep(0);
      setResult(null);
      setContact({ name: "", email: "", telefon: "", consent: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="preisrechner"
      data-testid="ai-calculator"
      className={`relative ${compact ? "" : "bg-white"}`}
    >
      <div className="absolute inset-0 blueprint-bg opacity-60 pointer-events-none" />
      <div className={`relative max-w-5xl mx-auto ${compact ? "" : "px-4 md:px-12 lg:px-16 py-24 md:py-32"}`}>
        {!compact && (
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2873B8] mb-4 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> KI-Preisrechner
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0C2749]" style={{ fontFamily: "Outfit" }}>
              Ihr Rohbau-Preis in <span className="text-[#2873B8]">60 Sekunden.</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mt-4 text-base">
              Geben Sie wenige Eckdaten ein – unsere KI analysiert Marktpreise, regionale Faktoren und Materialindex und liefert eine belastbare Indikation.
            </p>
          </div>
        )}

        <div className="bg-white border border-slate-200 shadow-2xl shadow-[#0C2749]/10 rounded-sm overflow-hidden">
          {/* Progress */}
          <div className="px-6 md:px-10 pt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium tracking-wide text-[#0C2749]">
                Schritt {Math.min(step + 1, STEPS.length)} von {STEPS.length} <span className="text-slate-400">· {STEPS[step]}</span>
              </p>
              <p className="text-xs text-slate-500">{Math.round(progress)}%</p>
            </div>
            <div className="h-1.5 bg-[#F5F5F5] overflow-hidden rounded-full">
              <div
                className="h-full bg-grad-primary transition-all duration-500"
                style={{ width: `${result ? 100 : progress}%` }}
                data-testid="calc-progress-bar"
              />
            </div>
          </div>

          <div className="px-6 md:px-10 py-10">
            {/* Result View */}
            {result ? (
              <div className="step-enter" data-testid="calc-result">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2873B8] mb-3 inline-flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> KI-Analyse abgeschlossen
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>
                  Geschätzter Rohbau-Preis
                </h3>

                <div className="bg-grad-primary text-white p-8 md:p-10 rounded-sm mb-6 relative overflow-hidden">
                  <div className="absolute inset-0 blueprint-bg-strong opacity-20" />
                  <div className="relative">
                    <p className="text-xs uppercase tracking-[0.25em] text-[#2C80C8] mb-3">Preisbereich (netto)</p>
                    <p className="text-4xl md:text-6xl font-bold tracking-tight count-pop" style={{ fontFamily: "Outfit" }} data-testid="calc-result-total">
                      {fmt(result.estimate.total_low)} – {fmt(result.estimate.total_high)} €
                    </p>
                    <p className="mt-3 text-white/80 text-sm">
                      ≈ {fmt(result.estimate.price_per_m2_low)} – {fmt(result.estimate.price_per_m2_high)} € pro m²
                      <span className="mx-2 text-[#2C80C8]">·</span>
                      Region: {data.standort}
                    </p>
                  </div>
                </div>

                <div className="bg-[#F5F5F5] border-l-2 border-[#2873B8] p-5 md:p-6 mb-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#2873B8] mb-2 inline-flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> KI-Empfehlung
                  </p>
                  <p className="text-sm md:text-base text-[#0C2749] leading-relaxed" data-testid="calc-result-recommendation">
                    {result.recommendation}
                  </p>
                </div>

                <div className="bg-white border border-slate-200 p-6 md:p-8 mb-6">
                  <h4 className="font-semibold text-[#0C2749] mb-4" style={{ fontFamily: "Outfit" }}>
                    Verbindliches Festpreisangebot anfordern
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field icon={User} placeholder="Ihr Name *" value={contact.name} onChange={(v) => setContact((c) => ({ ...c, name: v }))} testid="calc-name" />
                    <Field icon={Mail} placeholder="E-Mail *" type="email" value={contact.email} onChange={(v) => setContact((c) => ({ ...c, email: v }))} testid="calc-email" />
                    <Field icon={Phone} placeholder="Telefon" value={contact.telefon} onChange={(v) => setContact((c) => ({ ...c, telefon: v }))} testid="calc-phone" />
                    <label className="flex items-start gap-2 text-xs text-slate-600 md:col-span-1">
                      <input
                        type="checkbox"
                        className="mt-1 accent-[#2873B8]"
                        checked={contact.consent}
                        onChange={(e) => setContact((c) => ({ ...c, consent: e.target.checked }))}
                        data-testid="calc-consent"
                      />
                      Ich akzeptiere die Datenschutzerklärung. Es gilt keine Verkaufsabsicht – nur ein qualifiziertes Erstangebot.
                    </label>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      onClick={submitLead}
                      disabled={submitting}
                      className="btn-shimmer flex-1 inline-flex items-center justify-center gap-2 bg-[#0C2749] hover:bg-[#2873B8] text-white px-7 py-4 rounded-sm font-medium transition-colors disabled:opacity-60"
                      data-testid="calc-submit-lead"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Festpreisangebot anfordern
                    </button>
                    <a
                      href={`tel:${COMPANY.phoneRaw}`}
                      className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-[#2873B8] text-[#0C2749] px-7 py-4 rounded-sm font-medium"
                      data-testid="calc-call"
                    >
                      <Phone className="w-4 h-4" /> Direkt anrufen
                    </a>
                  </div>
                </div>

                <button
                  onClick={() => { setResult(null); setStep(0); }}
                  className="text-sm text-slate-500 hover:text-[#2873B8]"
                  data-testid="calc-restart"
                >
                  ← Neue Berechnung starten
                </button>
              </div>
            ) : (
              <>
                {/* Step 1 */}
                {step === 0 && (
                  <div className="step-enter">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>
                      Welcher Gebäudetyp?
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {GEBAEUDE.map((g) => {
                        const active = data.gebaeudetyp === g.id;
                        return (
                          <button
                            key={g.id}
                            data-testid={`calc-gebaeude-${g.id}`}
                            onClick={() => setData((d) => ({ ...d, gebaeudetyp: g.id }))}
                            className={`text-left p-5 border-2 transition-all rounded-sm flex items-start gap-4 ${
                              active ? "border-[#0C2749] bg-[#F5F5F5]" : "border-slate-200 hover:border-[#2873B8]"
                            }`}
                          >
                            <span className={`inline-flex h-11 w-11 items-center justify-center rounded-sm ${active ? "bg-grad-primary text-white" : "bg-[#F5F5F5] text-[#2873B8]"}`}>
                              <g.icon className="w-5 h-5" />
                            </span>
                            <span className="flex-1">
                              <span className="block font-semibold text-[#0C2749]" style={{ fontFamily: "Outfit" }}>{g.label}</span>
                              <span className="block text-xs text-slate-500 mt-1">{g.hint}</span>
                            </span>
                            {active && <Check className="w-5 h-5 text-[#2873B8]" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step 2 */}
                {step === 1 && (
                  <div className="step-enter">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>
                      Welche Bauweise?
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {BAUART.map((b) => {
                        const active = data.bauart === b.id;
                        return (
                          <button
                            key={b.id}
                            data-testid={`calc-bauart-${b.id}`}
                            onClick={() => setData((d) => ({ ...d, bauart: b.id }))}
                            className={`p-5 border-2 transition-all text-left rounded-sm ${
                              active ? "border-[#0C2749] bg-[#F5F5F5]" : "border-slate-200 hover:border-[#2873B8]"
                            }`}
                          >
                            <p className="font-semibold text-[#0C2749]" style={{ fontFamily: "Outfit" }}>{b.label}</p>
                            <p className="text-xs text-slate-500 mt-1">{b.hint}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step 3 */}
                {step === 2 && (
                  <div className="step-enter">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>
                      Größe & Standort
                    </h3>
                    <div className="mb-8">
                      <div className="flex items-end justify-between mb-3">
                        <label className="text-sm font-medium text-[#0C2749]">Wohn-/Nutzfläche</label>
                        <p className="text-3xl font-bold text-[#0C2749]" style={{ fontFamily: "Outfit" }}>
                          {data.quadratmeter} m²
                        </p>
                      </div>
                      <input
                        type="range"
                        min={50}
                        max={1500}
                        step={5}
                        value={data.quadratmeter}
                        onChange={(e) => setData((d) => ({ ...d, quadratmeter: Number(e.target.value) }))}
                        className="w-full accent-[#2873B8]"
                        data-testid="calc-qm-slider"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-2">
                        <span>50 m²</span><span>1.500 m²</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#0C2749] mb-3 block">Standort</label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {STANDORT.map((s) => {
                          const active = data.standort === s;
                          return (
                            <button
                              key={s}
                              data-testid={`calc-standort-${s.toLowerCase()}`}
                              onClick={() => setData((d) => ({ ...d, standort: s }))}
                              className={`p-3 text-sm border-2 rounded-sm transition-all ${
                                active ? "border-[#0C2749] bg-[#F5F5F5] text-[#0C2749] font-semibold" : "border-slate-200 hover:border-[#2873B8] text-slate-600"
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4 */}
                {step === 3 && (
                  <div className="step-enter">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>
                      Qualität & Zusatzleistungen
                    </h3>
                    <div className="mb-8">
                      <label className="text-sm font-medium text-[#0C2749] mb-3 block">Qualitätsstufe</label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {QUALITAET.map((q) => {
                          const active = data.qualitaetsstufe === q.id;
                          return (
                            <button
                              key={q.id}
                              data-testid={`calc-qualitaet-${q.id}`}
                              onClick={() => setData((d) => ({ ...d, qualitaetsstufe: q.id }))}
                              className={`p-5 border-2 text-left rounded-sm transition-all ${
                                active ? "border-[#0C2749] bg-[#F5F5F5]" : "border-slate-200 hover:border-[#2873B8]"
                              }`}
                            >
                              <p className="font-semibold text-[#0C2749]" style={{ fontFamily: "Outfit" }}>{q.label}</p>
                              <p className="text-xs text-slate-500 mt-1">{q.hint}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#0C2749] mb-3 block">Zusatzleistungen (optional)</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {ZUSATZ.map((z) => {
                          const active = data.leistungen.includes(z.id);
                          return (
                            <button
                              key={z.id}
                              data-testid={`calc-zusatz-${z.id}`}
                              onClick={() => toggleZusatz(z.id)}
                              className={`p-3 border-2 text-sm rounded-sm transition-all flex items-center gap-2 ${
                                active ? "border-[#0C2749] bg-[#F5F5F5] text-[#0C2749]" : "border-slate-200 hover:border-[#2873B8] text-slate-600"
                              }`}
                            >
                              <z.icon className="w-4 h-4" />
                              {z.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5 - Contact (optional pre-result) */}
                {step === 4 && (
                  <div className="step-enter">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#0C2749] mb-2" style={{ fontFamily: "Outfit" }}>
                      Fertig. Wohin senden wir die Schätzung?
                    </h3>
                    <p className="text-sm text-slate-600 mb-6">
                      Optional: Hinterlassen Sie Ihre Daten – wir melden uns mit einem präzisen Angebot. Sie können auch direkt zur Berechnung springen.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field icon={User} placeholder="Ihr Name" value={contact.name} onChange={(v) => setContact((c) => ({ ...c, name: v }))} testid="calc-precontact-name" />
                      <Field icon={Mail} placeholder="E-Mail" type="email" value={contact.email} onChange={(v) => setContact((c) => ({ ...c, email: v }))} testid="calc-precontact-email" />
                      <Field icon={Phone} placeholder="Telefon" value={contact.telefon} onChange={(v) => setContact((c) => ({ ...c, telefon: v }))} testid="calc-precontact-phone" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {!result && (
            <div className="px-6 md:px-10 py-6 bg-[#F5F5F5] border-t border-slate-200 flex items-center justify-between gap-4">
              <button
                onClick={prev}
                disabled={step === 0}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#0C2749] disabled:opacity-30"
                data-testid="calc-prev"
              >
                <ArrowLeft className="w-4 h-4" /> Zurück
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  onClick={next}
                  className="btn-shimmer inline-flex items-center gap-2 bg-[#0C2749] hover:bg-[#2873B8] text-white px-6 py-3 rounded-sm font-medium transition-colors"
                  data-testid="calc-next"
                >
                  Weiter <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={calculate}
                  disabled={calculating}
                  className="btn-shimmer inline-flex items-center gap-2 bg-grad-primary text-white px-7 py-3 rounded-sm font-medium disabled:opacity-60"
                  data-testid="calc-calculate"
                >
                  {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
                  Preis berechnen
                </button>
              )}
            </div>
          )}
        </div>

        {!compact && (
          <p className="text-center text-xs text-slate-500 mt-6 max-w-2xl mx-auto">
            Hinweis: Die Schätzung ist eine erste Indikation auf Basis aktueller Marktpreise und regionaler Faktoren. Verbindliche Angebote nach Vor-Ort-Besichtigung.
          </p>
        )}
      </div>
    </section>
  );
}

function Field({ icon: Icon, placeholder, type = "text", value, onChange, testid }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-3 py-3 border border-slate-200 focus:border-[#2873B8] focus:outline-none rounded-sm bg-white text-[#0C2749] text-sm"
        data-testid={testid}
      />
    </div>
  );
}
